import moment from "moment";
import isEmail from "validator/lib/isEmail";
import isValidUrl from "validator/lib/isURL";
import isPostalCode from "validator/lib/isPostalCode";
import CommonMixin from "../Mixins/common";

import { mapActions } from "vuex";
import { mapFields } from "vuex-map-fields";
import { networkSecurityOneState } from "../store/cyberSecurityCapabilities";
import { phishingTrainingState } from "../store/organizationLandscape";
import localService from "@/services/localService";

import RequestQueue from "@/helpers/requestQueue";
const ApiQueue = new RequestQueue();

function isValidVendor(el) {
  return el.name?.trim().length && el.website?.trim().length && isValidUrl(el.website);
}

function isValidVendorWithType(el) {
  return el.name?.trim().length && el.website?.trim().length && el.type && isValidUrl(el.website);
}

export default {
  mixins: [CommonMixin],

  computed: {
    ...mapFields("login", ["currentUser"]),

    ...mapFields("indicationSetup", ["indicationSetupSteps"]),

    ...mapFields("companySetup", [
      "debounce",
      "companySetupSteps",
      "teamMembers",
      "fetchingAnswers",
      "questionnaire",
      "showInsurabilityMenuItem",
      "currentQuestionnaireVersion",
      "initialQuestionnaireAllScreenData",
      "brokersList",
    ]),

    ...mapFields("quickStart", ["quickStartSteps"]),

    ...mapFields("buildTeam", [
      "inviteCoAdmins",
      "technologyTeammates",
      "cyberSecurityTeammates",
      "operationProcessTeammates",
      "hrTrainingTeammates",
      "legalContractTeammates",
      "cyberInsuranceBrokers",
    ]),

    ...mapFields("buildCompanyProfile", [
      "policiesPlanningQuestions",
      "companyDetailsQuestions",
      "companyWebsitesQuestions",
      "companyLogoQuestions",
      "industryDetailsQuestions",
      "employeesDetailsQuestions",
      "cyberSecurityInvestmentQuestions",
      "revenueGrowthQuestions",
      "revenueProductsQuestions",
      "revenueCustomersQuestions",
    ]),

    ...mapFields("organizationalLandscape", [
      "emailSecurityQuestions",
      "emailSecurityControlsQuestions",
      "phishingTrainingQuestions",
      "physicalHardwareQuestions",
      "backupCapabilitiesQuestions",
      "backupContinuityQuestions",
      "serviceAccountQuestions",
      "chosenDevicesAndSystemsQuestions",
      "applicationInfrastructureQuestions",
      "onPremiseInfrastructureQuestions",
      "legacySoftwareInUseQuestions",
      "personallyIdentifiableQuestions",
      "mediaUsageQuestions",
      "financialControlsQuestions",
      "telecommunicationQuestions",
      "paymentCardsQuestions",
      "privacyConcernsQuestions",
      "orgDataBackedUpQuestions",
      "orgSecurityHandleQuestions",
      "orgRecentIncidentsExperiencedQuestions",
      "systemRecordsProtectQuestions",
    ]),

    ...mapFields("cyberSecurityCapabilities", [
      "cyberSecurityOrganizationQuestions",
      "cyberSecurityVendorsQuestions",
      "networkSecurityOneQuestions",
      "networkSecurityTwoQuestions",
      "networkSecurityCapabilitiesQuestions",
      "networkSecurityControlsQuestions",
      "generalSecurityQuestions",
      "vulnerabilityScanningQuestions",
    ]),

    isValidPostalCode() {
      return (postal) => {
        return postal ? isPostalCode(postal, "US") : null;
      };
    },

    coverageBrokerDetails() {
      let broker = this.brokersList?.length ? this.brokersList[0] : null;
      return broker;
    },

    isAccountFree() {
      return this.currentUser.user.payment_complete === 0;
    },

    isNetworkSecurityCapabilitiesFilled() {
      const questions = this.networkSecurityCapabilitiesQuestions?.questions;
      if (!questions?.length) return false;
      const answers = [];
      const keys = new Set([
        "do_you_have_endpoint_detection_&_response_edr",
        "do_you_have_security_operations_center_soc",
        "do_you_have_virtual_network_computing_vnc_or_remote_desktop_protocol_rdp",
      ]);

      for (const question of questions) {
        if (keys.has(question.key)) {
          answers.push(question.choice);
        }
      }

      return answers.includes("yes");
    },

    showInsurabilityReport() {
      return this.showInsurabilityMenuItem || this.questionnaire?.extraDetails?.userActivities?.csgStarted || false;
    },
  },

  data() {
    return {
      // All the screens which are sent to the api
      allScreens: {
        "invite-co-admins": "inviteCoAdmins",
        "add-technology-expertise": "technologyTeammates",
        "add-cybersecurity-expertise": "cyberSecurityTeammates",
        "add-operations-process-expertise": "operationProcessTeammates",
        "add-hr-training-expertise": "hrTrainingTeammates",
        "add-legal-contract-expertise": "legalContractTeammates",
        "add-cyber-insurance-broker": "cyberInsuranceBrokers",
        "policies-planning": "policiesPlanningQuestions",
        "company-details": "companyDetailsQuestions",
        "company-websites": "companyWebsitesQuestions",
        "company-logo": "companyLogoQuestions",
        "industry-details": "industryDetailsQuestions",
        "employees-details": "employeesDetailsQuestions",
        "cybersecurity-investment": "cyberSecurityInvestmentQuestions",
        "revenue-growth": "revenueGrowthQuestions",
        "revenue-products": "revenueProductsQuestions",
        "revenue-customers": "revenueCustomersQuestions",
        phishing: "phishingTrainingQuestions",
        "email-security": "emailSecurityQuestions",
        "email-security-controls": "emailSecurityControlsQuestions",
        "physical-hardware-on-premise": "physicalHardwareQuestions",
        "backup-capabilities": "backupCapabilitiesQuestions",
        "backups-and-business-continuity": "backupContinuityQuestions",
        "service-accounts": "serviceAccountQuestions",
        "cyber-security-organization": "cyberSecurityOrganizationQuestions",
        "network-security-one": "networkSecurityOneQuestions",
        "network-security-two": "networkSecurityTwoQuestions",
        "network-security-capabilities": "networkSecurityCapabilitiesQuestions",
        "network-security-controls": "networkSecurityControlsQuestions",
        "general-security-controls": "generalSecurityQuestions",
        "vulnerability-scanning": "vulnerabilityScanningQuestions",
        "devices-systems-used-remotely": "chosenDevicesAndSystemsQuestions",
        "applications-infrastructure-cloud": "applicationInfrastructureQuestions",
        "on-premise-infrastructure-software": "onPremiseInfrastructureQuestions",
        "legacy-software": "legacySoftwareInUseQuestions",
        "personally-identifiable-information": "personallyIdentifiableQuestions",
        "media-usage": "mediaUsageQuestions",
        "financial-controls": "financialControlsQuestions",
        telecommunication: "telecommunicationQuestions",
        "payment-cards": "paymentCardsQuestions",
        "privacy-concerns": "privacyConcernsQuestions",
        "cyber-security-vendors": "cyberSecurityVendorsQuestions",
        "systems-records-for-protect-them": "systemRecordsProtectQuestions",
        "org-data-backed-up": "orgDataBackedUpQuestions",
        "org-security-handle": "orgSecurityHandleQuestions",
        "org-recent-incidents-experienced": "orgRecentIncidentsExperiencedQuestions",
      },

      defaultStates: {
        networkSecurityOneState: networkSecurityOneState(),
        phishingTrainingState: phishingTrainingState(),
      },
    };
  },

  methods: {
    ...mapActions("companySetup", ["saveQuestionnaire", "getQuestionnaire"]),

    _createIndexMap(items) {
      const indexMap = {};
      for (const [index, item] of items.entries()) {
        indexMap[item.id] = index;
      }
      return indexMap;
    },
    _validateArrayShowOn(item, val) {
      if (!Array.isArray(item.showOn) || !val?.length) {
        return null;
      }
      if (Array.isArray(val) && val.length) {
        // IF VAL WHICH IS THE ANSWER IS TYPE = ARRAY
        return val.some((choice) => item.showOn.includes(choice));
      }
      // IF VAL WHICH IS THE ANSWER IS TYPE = STRING
      return item.showOn.includes(val);
    },
    _validateRangeShowOn(item, val) {
      if (!item.hasOwnProperty("type")) {
        return null;
      }
      const [lowerLimit, upperLimit] = item.showOn.split(",");
      const value = Number.parseInt(val, 10);
      return value > Number.parseInt(lowerLimit, 10) && value <= Number.parseInt(upperLimit, 10);
    },
    _validateSimpleShowOn(item, val) {
      return val === item.showOn;
    },
    _resetDependantQuestions(objCopy, key, ind, dependants, indexMap, stateKeyName) {
      if (!this.defaultStates?.[stateKeyName]) {
        return;
      }

      // Reset Main Dependant Question to default
      if (objCopy[key]?.[ind] && this.defaultStates[stateKeyName][ind]) {
        objCopy[key][ind] = this.defaultStates[stateKeyName][ind];
      }

      // Reset dependant questions dependants
      if (dependants?.length) {
        for (const question of dependants) {
          const questionIndex = indexMap[question.id];
          if (
            questionIndex !== undefined &&
            objCopy[key]?.[questionIndex] &&
            this.defaultStates[stateKeyName][questionIndex]
          ) {
            objCopy[key][questionIndex] = this.defaultStates[stateKeyName][questionIndex];
          }
        }
      }
    },
    _processShowOnItem(item, objCopy, key, indexMap, val, stateKeyName) {
      const ind = indexMap[item.id];

      if (ind === undefined || !objCopy[key]?.[ind]) {
        return;
      }

      const dependants = objCopy[key][ind]["show"];

      // CHOICE AND SHOW ON CAN ALSO BE AN ARRAY i.e a question can be shown on multiple choices
      const arrayValidation = this._validateArrayShowOn(item, val);
      if (arrayValidation !== null) {
        objCopy[key][ind].visible = arrayValidation;
        return;
      }

      // IF THE DEPENDANT NEEDS TO BE CHECKED WITHIN A RANGE FOR EX BETWEEN 0 to 100
      const rangeValidation = this._validateRangeShowOn(item, val);
      if (rangeValidation !== null) {
        objCopy[key][ind].visible = rangeValidation;
        return;
      }

      // IT CAN ALSO BE A SIMPLE STRING YES|NO
      const isValid = this._validateSimpleShowOn(item, val);
      objCopy[key][ind].visible = isValid;

      if (!isValid && stateKeyName) {
        this._resetDependantQuestions(objCopy, key, ind, dependants, indexMap, stateKeyName);
      }
    },
    handleQuestionToggling(obj, key, index, val, stateKeyName) {
      const objCopy = structuredClone(obj);
      const showIds = objCopy[key][index].show;

      if (!showIds?.length) {
        return objCopy;
      }

      // Create a completely new questions array to ensure Vue reactivity
      // First, create a set of question IDs that should be visible
      const visibleQuestionIds = new Set();
      for (const item of showIds) {
        const arrayValidation = this._validateArrayShowOn(item, val);
        const rangeValidation = this._validateRangeShowOn(item, val);
        const simpleValidation = this._validateSimpleShowOn(item, val);

        const shouldBeVisible = arrayValidation ?? rangeValidation ?? simpleValidation;

        if (shouldBeVisible) {
          visibleQuestionIds.add(item.id);
        }
      }

      const newQuestions = objCopy[key].map((question) => {
        const showItem = showIds.find((item) => item.id === question.id);

        if (showItem) {
          const shouldBeVisible = visibleQuestionIds.has(question.id);
          return { ...question, visible: shouldBeVisible };
        }

        return { ...question };
      });

      objCopy[key] = newQuestions;

      return objCopy;
    },

    getClickedOption(object, key, val, index, stateKey) {
      let obj = structuredClone(object);

      let showIds = obj[key][index].show;

      if (showIds?.length) obj = this.handleQuestionToggling(obj, key, index, val, stateKey);

      obj[key][index].choice = val;

      return this._applyCanonicalDependencyVisibility(obj, key);
    },

    /* 
      This function is responsible for updating a property inside an object.
      It updates the object passed to it with the data.

      Arguments: 
        1. object = the object that needs to be updated.
        2. parentKey = which key in object needs to be updated, it is usually an array.
        3. index = which index of selected object[parentKey].
        4. childKey = which key in the selected index of that array. e.g, object['questions'][1][choice].
        5. data = the data which is supposed to replace the existing one.
    */
    updateProp(object, parentKey, index, childKey, data) {
      let obj = structuredClone(object);

      // WILL ONLY RUN WHEN THERE ARE DEPENDENCIES ON A QUESTION
      let showIds = obj[parentKey][index].show;

      if (showIds?.length) obj = this.handleQuestionToggling(obj, parentKey, index, data);

      obj[parentKey][index][childKey] = data;
      return this._applyCanonicalDependencyVisibility(obj, parentKey);
    },

    //
    // WILL CHECK IF THE USER SUPPLIED FORM VALUES AS PER THE RULES DEFINED IN THE FORM DATA
    //

    _shouldValidateField(el, valueKey) {
      const isIndicationSetup = this.$route.path.includes("indication-setup");
      if (!isIndicationSetup) {
        return true;
      }
      return !el.hiddenIndicationFormData?.includes(valueKey);
    },
    _validateField(el, key, value, valueKey, valueType, validationType, invalid) {
      if (!this._shouldValidateField(el, valueKey)) {
        return;
      }
      if (this.hasFormInvalidData(key, value, valueKey, valueType, validationType)) {
        invalid.push(el);
      }
    },
    _processDeepRequiredFormData(el, key, invalid) {
      for (const formKey of Object.keys(el.requiredFormData[key])) {
        const value = el.formData[key][formKey];
        const fieldConfig = el.requiredFormData[key][formKey];
        const validationType = fieldConfig.validationType;
        const valueType = fieldConfig.type;
        const valueKey = fieldConfig["key"];

        this._validateField(el, key, value, valueKey, valueType, validationType, invalid);
      }
    },
    _processRegularRequiredFormData(el, key, invalid) {
      const value = el.formData[key];
      const fieldConfig = el.requiredFormData[key];
      const validationType = fieldConfig.validationType;
      const valueType = fieldConfig.type;
      const valueKey = fieldConfig["key"];

      this._validateField(el, key, value, valueKey, valueType, validationType, invalid);
    },
    isInvalidForm(el) {
      if (!el) return;

      const invalid = [];

      // LOOP THROUGH THE FORM DATA AND CHECK IF CORRECT AND VALID VALUES WERE PROVIDED BY THE USER
      for (let key in el.formData) {
        if (!el.requiredFormData[key]) {
          continue;
        }

        const isDeepRequired = el.deepRequiredFormData?.includes(key);
        if (isDeepRequired) {
          this._processDeepRequiredFormData(el, key, invalid);
        } else {
          this._processRegularRequiredFormData(el, key, invalid);
        }
      }

      return invalid.length > 0;
    },

    isValidUSAPhoneNumber(phoneNumber) {
      // Define a regular expression to match the pattern
      const regex = /^\([2-9]\d{2}\) \d{3}-\d{4}$/;
      return regex.test(phoneNumber);
    },

    _isValueEmpty(value) {
      return !value || (value && !value?.toString()?.trim().length);
    },
    _isArrayItemEmpty(val, key, valueKey) {
      return !val[key] || (val[valueKey] && !val[valueKey]?.trim().length);
    },
    _validateRequired(key, value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => this._isArrayItemEmpty(val, key, valueKey));
      }
      return this._isValueEmpty(value);
    },
    _validateNumberValue(key, value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => {
          if (this._isArrayItemEmpty(val, key, valueKey)) {
            return true;
          }
          return !this.formatStringToNumber(val[valueKey]);
        });
      }
      if (this._isValueEmpty(value)) {
        return true;
      }
      return !this.formatStringToNumber(value);
    },
    _validateEmail(value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => !isEmail(val[valueKey]));
      }
      return !isEmail(value);
    },
    _validateUrl(value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => val[valueKey] && !isValidUrl(val[valueKey]));
      }
      return !isValidUrl(value);
    },
    _validatePostalCode(value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => val[valueKey] && !this.isValidPostalCode(val[valueKey]));
      }
      return !this.isValidPostalCode(value);
    },
    _validatePastDate(value, valueKey, valueType) {
      if (valueType === "Array") {
        return value.some((val) => !this.isPastDate(val[valueKey]));
      }
      return !this.isPastDate(value);
    },
    _validatePastYear(value, valueType) {
      if (valueType === "Array") {
        return value.some(() => !this.isPastYear(value));
      }
      return !this.isPastYear(value);
    },
    _validateDateValidAndInFormat(value) {
      return !this.isDateValidAndInFormat(value);
    },
    _validatePhoneNumber(value) {
      return !this.isValidUSAPhoneNumber(value);
    },
    _validateFein(value) {
      return !this.isFein(value);
    },
    _getValidationFunction(validationType) {
      const validators = {
        required: this._validateRequired.bind(this),
        isNumberValue: this._validateNumberValue.bind(this),
        isEmail: this._validateEmail.bind(this),
        isUrl: this._validateUrl.bind(this),
        isPostalCode: this._validatePostalCode.bind(this),
        isPastDate: this._validatePastDate.bind(this),
        isPastYear: this._validatePastYear.bind(this),
        isDateValidAndInFormat: this._validateDateValidAndInFormat.bind(this),
        isPhoneNumber: this._validatePhoneNumber.bind(this),
        isFein: this._validateFein.bind(this),
      };
      return validators[validationType];
    },
    hasFormInvalidData(key, value, valueKey, valueType, validationType) {
      if (valueType === "Array" && !Array.isArray(value)) {
        return false;
      }

      const validator = this._getValidationFunction(validationType);
      if (!validator) {
        return false;
      }

      if (validationType === "required" || validationType === "isNumberValue") {
        return validator(key, value, valueKey, valueType);
      }

      if (validationType === "isPastYear") {
        return validator(value, valueType);
      }

      if (
        validationType === "isDateValidAndInFormat" ||
        validationType === "isPhoneNumber" ||
        validationType === "isFein"
      ) {
        if (this._isValueEmpty(value)) {
          return true;
        }
        return validator(value);
      }

      if (this._isValueEmpty(value)) {
        return true;
      }

      return validator(value, valueKey, valueType);
    },
    //
    // WILL CHECK IF ALL THE QUESTIONS WERE ANSWERED AS PER THE REQUIREMENT OF THE PAGE
    //

    _hasFormData(el) {
      return el.visible && el.formData && el.requiredFormData && Object.keys(el.requiredFormData)?.length;
    },
    _checkFormDataValidation(el, key, callBack, isUnanswered) {
      if (!this._hasFormData(el)) {
        return;
      }

      const isChoiceType = el[key] && el[key] === el.formVisibleOn;
      const isNonChoiceType = !el[key] && !el.formVisibleOn;

      if ((isChoiceType || isNonChoiceType) && this.isInvalidForm(el)) {
        isUnanswered.push(el);
      }

      if (callBack !== undefined && !callBack()) {
        isUnanswered.push(el);
      }
    },
    _shouldCheckQuestion(el) {
      const isIndicationSetupHidden = this.$route.path.includes("indication-setup") && el.indicationSetupHidden;
      const isBuildRiskProfile = this.$route.path.includes("build-risk-profile") && el.indicationAdditionalInfoShow;
      const isInsurabilitySetup = this.$route.path.includes("insurability-setup") && el.insurabilitySetupVisible;

      return !isIndicationSetupHidden || isBuildRiskProfile || isInsurabilitySetup;
    },
    _isQuestionVisible(el) {
      if (!el.visible) {
        return false;
      }

      const showQuickQuotes = this.currentUser.user.showQuickQuotes;
      const isBuildRiskProfile = this.$route.path.includes("build-risk-profile");
      const isInsurabilitySetup = this.$route.path.includes("insurability-setup");

      return (
        (!showQuickQuotes && !el.csgHidden) ||
        (showQuickQuotes && !el.quickStartHidden) ||
        (el.csgHidden && el.indicationAdditionalInfoShow && isBuildRiskProfile) ||
        (isInsurabilitySetup && el.insurabilitySetupVisible)
      );
    },
    _checkBasicQuestionValidation(el, key, isUnanswered) {
      if (!this._isQuestionVisible(el) || el?.optional) {
        return;
      }

      if (el[key] === null) {
        isUnanswered.push(el);
        return;
      }

      if (Array.isArray(el[key]) && el[key].filter(Boolean).length === 0) {
        isUnanswered.push(el);
      }
    },
    _checkTypeSpecificValidation(el, key, isUnanswered) {
      if (!el.visible || el?.optional) {
        return;
      }

      const typeValidators = {
        hasVendors: () => !this.areVendorsValid(el.choice),
        hasWebAddress: () => el[key] && !isValidUrl(el[key]),
        hasVendorsWithType: () => !this.areVendorsValid(el.choice, true),
        hasProviders: () => !this.areProvidersValid(el.choice),
        isNumberValue: () => el.choice && !this.formatStringToNumber(el.choice),
      };

      const validator = typeValidators[el.type];
      if (validator?.()) {
        isUnanswered.push(el);
      }

      if (el.validationType === "isPastYear" && !this.isPastYear(el.choice)) {
        isUnanswered.push(el);
      }
    },
    areAllQuestionsAnswered(itemsArray, callBack, key = "choice") {
      const isUnanswered = [];

      for (const el of itemsArray) {
        this._checkFormDataValidation(el, key, callBack, isUnanswered);

        if (this._shouldCheckQuestion(el)) {
          this._checkBasicQuestionValidation(el, key, isUnanswered);
          this._checkTypeSpecificValidation(el, key, isUnanswered);
        }
      }

      return isUnanswered.length === 0;
    },

    areCompanyWebsitesValid() {
      const questions = this.companyWebsitesQuestions?.questions;
      if (!questions?.length) return true;

      // FUNCTION TO GET A QUESTION BY KEY
      const getQuestion = (key) => questions.find((e) => e.key === key);

      // FUNCTION TO VALIDATE WEBSITES
      const validateWebsites = (websites) => {
        if (!websites || websites.length === 0) return true;
        if (websites.length === 1 && (!websites[0].web_address || websites[0].web_address.trim() === "")) return true;
        return websites.every((site) => isValidUrl(site.web_address));
      };

      const hasWebsitesPublicly = getQuestion("does_organization_have_websites_publicly").choice === "yes";

      // If answer is no return true because nothing was answered by the user to validate
      if (!hasWebsitesPublicly) return true;

      const primaryWebAddress = getQuestion("primary_web_address")?.choice;

      // If primary web address is invalid return false
      if (primaryWebAddress && !isValidUrl(primaryWebAddress)) return false;

      const { company_owned_websites: companyWebsites = [] } = getQuestion("company_owned_websites")?.formData || {};

      const { honey_pot_websites: honeyPotWebsites = [] } = getQuestion("honey_pot_websites")?.formData || {};

      if (!validateWebsites(companyWebsites)) return false;

      if (!validateWebsites(honeyPotWebsites)) return false;

      // Check for duplicates in all categories
      const allWebsites = [
        ...honeyPotWebsites.map((el) => this.getDomainDetailsFromUrl(el.web_address)).filter(Boolean),
        ...companyWebsites.map((el) => this.getDomainDetailsFromUrl(el.web_address)).filter(Boolean),
        this.getDomainDetailsFromUrl(primaryWebAddress),
      ].filter(Boolean);

      const hasDuplicates = new Set(allWebsites).size !== allWebsites.length;

      if (hasDuplicates) return false;

      return true;
    },

    getDomainDetailsFromUrl(webAddress) {
      if (!webAddress) return null;
      let address = webAddress.replaceAll(/^https?:\/\//g, "").replaceAll(/^www\./g, "");
      return address.toLowerCase();
    },

    revenueGrowthCustomValidation() {
      let isValid = true;
      const businessActivitiesQuestion = this.revenueGrowthQuestions.questions.find(
        (e) => e.key == "does_organization_engage_following_business_activities"
      );
      if (businessActivitiesQuestion) {
        isValid = false;
        for (const key of Object.keys(businessActivitiesQuestion.formData)) {
          if (businessActivitiesQuestion.formData[key]) {
            isValid = true;
          }
        }
      }
      return isValid;
    },

    areProvidersValid(choices) {
      return choices?.every((item) => {
        if (item.choice === null) return false;
        if (item.hasOwnProperty("description") && (item.description === null || item.description.trim() === ""))
          return false;
        return true;
      });
    },

    handlePlatformClickWithNone(itemsArray, key, noneKeyName = "none") {
      let arr = structuredClone(itemsArray) || [];

      if (key === noneKeyName) {
        arr = arr.includes(noneKeyName) ? [] : [key];
      } else {
        if (arr.includes(noneKeyName)) {
          arr = arr.filter((el) => el !== noneKeyName);
        }

        arr = arr.includes(key) ? arr.filter((el) => el !== key) : [...arr, key];
      }

      return arr;
    },

    areVendorsValid(vendors, isType = false) {
      const func = isType ? isValidVendorWithType : isValidVendor;
      return vendors?.filter(func).length === vendors?.length;
    },

    getQuestionnairePayload() {
      const payload = {};

      for (const screen of Object.values(this.allScreens)) {
        if (!this[screen]) continue;
        payload[screen] = this.makePayloadForServer(this[screen]);
      }

      return payload;
    },

    saveAnswers(organizationId = null) {
      if (this.readOnlyView) {
        return;
      }
      const payload = {};

      for (const screen of Object.values(this.allScreens)) {
        if (!this[screen]) continue;
        payload[screen] = this.makePayloadForServer(this[screen], screen);
      }

      if (this.questionnaire?.markets) {
        payload["markets"] = this.questionnaire.markets;
      }

      if (this.questionnaire?.extraDetails) {
        payload["extraDetails"] = this.questionnaire.extraDetails;
      }

      const updatedPayload = this.getFlattenedPhoneNumbers(payload);

      clearTimeout(this.debounce);

      return new Promise((resolve) => {
        this.debounce = setTimeout(() => {
          ApiQueue.enqueue(() =>
            this.saveQuestionnaire({ questionnaire: updatedPayload, organizationId: organizationId })
          ).then(resolve);
        }, 350);
      });
    },

    async saveExtraDetails(organizationId = null) {
      if (this.readOnlyView) return;
      if (!this.questionnaire?.extraDetails) return;
      // Metadata-only: do not resave full questionnaire or markets. API merges.
      return this.saveQuestionnaire({
        questionnaire: { extraDetails: this.questionnaire.extraDetails },
        organizationId,
      });
    },

    setInitialQuestionnaireDataForAllScreen() {
      if (this.initialQuestionnaireAllScreenData) return;
      this.initialQuestionnaireAllScreenData = {};
      let screen = null;
      for (const keyName of Object.keys(this.allScreens)) {
        screen = this.allScreens[keyName];
        if (this[screen]) {
          this.initialQuestionnaireAllScreenData[screen] = structuredClone(this[screen]);
        }
      }
    },

    resetQuestionnaireToInitialState() {
      if (!this.initialQuestionnaireAllScreenData) return;
      let screen = null;
      for (const keyName of Object.keys(this.allScreens)) {
        screen = this.allScreens[keyName];
        this[screen] = structuredClone(this.initialQuestionnaireAllScreenData[screen]);
      }
    },

    async getAnswers(organizationId = null, options = {}) {
      const hydrateScreens = options.hydrateScreens === true;
      this.fetchingAnswers = true;

      const data = await this.getQuestionnaire(organizationId);

      this.questionnaire = data?.company_setup?.questionnaire || null;
      this.currentQuestionnaireVersion = data?.company_setup?.version;

      if (!this.questionnaire) {
        this.fetchingAnswers = false;
        return;
      }

      if (!hydrateScreens) {
        this.fetchingAnswers = false;
        return data.company_setup;
      }

      this.setInitialQuestionnaireDataForAllScreen();
      this.resetQuestionnaireToInitialState();

      let screen = null;

      for (const keyName of Object.keys(this.allScreens)) {
        screen = this.allScreens[keyName];
        if (this.questionnaire[screen]) {
          this[screen] = this.setPayloadFromServer(this[screen], this.questionnaire[screen]);

          this.companySetupSteps.map((val) => {
            if (val.details?.find((e) => e.key == keyName)) {
              val.details.find((e) => e.key == keyName).status = this.questionnaire[screen].completed ? 1 : 0;
              val.details.find((e) => e.key == keyName).hidden = this.questionnaire[screen].hidden ? 1 : 0;
            }
          });

          this.quickStartSteps.map((val) => {
            if (val.details?.find((e) => e.key == keyName)) {
              val.details.find((e) => e.key == keyName).status = this.questionnaire[screen].completed ? 1 : 0;
              val.details.find((e) => e.key == keyName).hidden = this.questionnaire[screen].hidden ? 1 : 0;
            }
          });

          this.indicationSetupSteps.map((val) => {
            if (val.details?.find((e) => e.key == keyName)) {
              val.details.find((e) => e.key == keyName).status = this.questionnaire[screen].completed ? 1 : 0;
              val.details.find((e) => e.key == keyName).hidden = this.questionnaire[screen].hidden ? 1 : 0;
            }
          });
        }
      }

      this.fetchingAnswers = false;

      return data.company_setup;
    },

    setQuestionStatus(keyName, isCompleted) {
      const screen = this.allScreens?.[keyName];
      if (!screen || !this[screen]) return;
      this[screen].completed = isCompleted;
    },

    _applyCanonicalDependencyVisibility(obj, key) {
      if (!obj?.[key]?.length) return obj;

      const byKey = this._createServerDict(obj[key]);
      obj[key] = obj[key].map((question) => {
        if (!question.canonicalDependency) return question;
        return {
          ...question,
          visible: this._evaluateCanonicalDependency(question.canonicalDependency, byKey),
        };
      });
      return obj;
    },

    _evaluateCanonicalDependency(dependency, byKey) {
      const conditions = dependency?.conditions || [];
      if (!conditions.length) return true;

      const results = conditions.map((condition) => {
        const choice = byKey[condition.parentKey]?.choice;
        const expected = condition.showOn;
        if (Array.isArray(choice)) return choice.includes(expected);
        if (typeof choice === "string") return choice.toLowerCase() === String(expected).toLowerCase();
        return choice === expected;
      });

      return results.reduce((acc, result, index) => {
        const operator = dependency.operators?.[index - 1] || "AND";
        return operator === "OR" ? acc || result : acc && result;
      });
    },

    // DONT SEND FULL PAYLOAD
    // SEND ONLY id, choice, visibility to the server to keep the payload compact
    makePayloadForServer(jsonObject, screen) {
      // IF THE PASSED OBJECT DOESN'T HAVE QUESTIONS JUST RETURN AS IS
      if (!jsonObject?.questions) return jsonObject;

      //  IF IT HAS QUESTIONS PROCESS AND SEND ONLY LIMITED DATA
      const obj = {
        ...jsonObject,

        questions: jsonObject.questions.map((el) => {
          let payload = { key: el.key };
          // if question has a choice then add it to the backend request
          if (el.hasOwnProperty("choice")) payload.choice = el.choice;

          // if question has a formdata then add it to the backend request
          if (el.hasOwnProperty("formData")) payload.formData = el.formData;

          if (el.canonicalQuestionId) payload.canonicalQuestionId = el.canonicalQuestionId;

          return payload;
        }),

        completed:
          screen && screen == "networkSecurityControlsQuestions" && !this.isNetworkSecurityCapabilitiesFilled
            ? 1
            : jsonObject.completed || 0,
        hidden:
          screen && screen == "networkSecurityControlsQuestions" && !this.isNetworkSecurityCapabilitiesFilled ? 1 : 0,
      };

      return obj;
    },

    _createServerDict(questions) {
      const serverDict = {};
      for (const question of questions) serverDict[question.key] = question;
      return serverDict;
    },
    _createKeyIndexMap(questions) {
      const indexes = {};
      for (const [index, el] of questions.entries()) indexes[el.key] = index;
      return indexes;
    },
    _getChoiceValue(question, serverDict) {
      if (!question.hasOwnProperty("choice")) {
        return null;
      }

      const serverQuestion = serverDict[question.key];
      const serverChoice = serverQuestion?.choice;
      if (serverQuestion?.hasOwnProperty("choice")) {
        return serverChoice;
      }

      const shouldPreserveChoice = question.type === "hasVendors" || question.type === "hasVendorsWithType";
      return shouldPreserveChoice ? question.choice : null;
    },
    _getHiddenIndicationFormData(question, serverDict) {
      if (!question.hasOwnProperty("hiddenIndicationFormData")) {
        return null;
      }
      return serverDict[question.key]?.hiddenIndicationFormData || question.hiddenIndicationFormData;
    },
    _mergeFormData(question, serverDict) {
      if (!question.hasOwnProperty("formData")) {
        return null;
      }

      const formData = serverDict[question.key]?.formData || question.formData;
      const mergedFormData = { ...formData };

      for (const questionKey of Object.keys(question.formData)) {
        if (!formData.hasOwnProperty(questionKey)) {
          mergedFormData[questionKey] = question.formData[questionKey];
        }
      }

      return mergedFormData;
    },
    _buildQuestionPayload(question, serverDict) {
      const payload = { key: question.key };

      if (this.readOnlyView) {
        payload.readOnly = true;
      }

      const choice = this._getChoiceValue(question, serverDict);
      if (choice !== null) {
        payload.choice = choice;
      }

      const hiddenIndicationFormData = this._getHiddenIndicationFormData(question, serverDict);
      if (hiddenIndicationFormData !== null) {
        payload.hiddenIndicationFormData = hiddenIndicationFormData;
      }

      const formData = this._mergeFormData(question, serverDict);
      if (formData !== null) {
        payload.formData = formData;
      }

      return this.getMaskedPhoneNumber(question, payload);
    },
    _updateQuestionVisibility(question, showItem, originalQuestions, indexes) {
      const index = indexes[showItem.key];
      if (!originalQuestions[index]) {
        return;
      }

      const { choice } = question;
      const { showOn } = showItem;

      // If answer and showOn both are array type
      if (choice && Array.isArray(choice) && choice.length && Array.isArray(showOn)) {
        originalQuestions[index].visible = choice.some((ele) => showOn.includes(ele));
        return;
      }

      // If answer is string but showOn is array type
      if (choice && !Array.isArray(choice) && Array.isArray(showOn)) {
        originalQuestions[index].visible = showOn.includes(choice);
        return;
      }

      // If answer is string but of type = range for eg 0 to 100
      if (choice && showItem.hasOwnProperty("type")) {
        const [lowerLimit, upperLimit] = showOn.split(",");
        const value = Number.parseInt(choice, 10);
        const valid = value > Number.parseInt(lowerLimit, 10) && value <= Number.parseInt(upperLimit, 10);
        originalQuestions[index].visible = valid;
        return;
      }

      // If answer and showOn are both string type
      originalQuestions[index].visible = choice === showOn;
    },
    _processQuestionVisibility(question, originalQuestions, indexes) {
      if (!question.show) {
        return;
      }

      for (const showItem of question.show) {
        this._updateQuestionVisibility(question, showItem, originalQuestions, indexes);
      }

      const isIndication = this.$route?.path.includes("indication-setup");
      if (isIndication && question.indicationAdditionalInfoShow && !question.visible) {
        question.visible = true;
      }
    },
    // UPDATE THE VUEX STORE
    setPayloadFromServer(original, serverPayload) {
      if (!serverPayload.questions) {
        return serverPayload;
      }

      let originalQuestions = [...original.questions];
      const serverDict = this._createServerDict(serverPayload.questions);
      const indexes = this._createKeyIndexMap(originalQuestions);

      const updated = originalQuestions.map((question) => {
        const payload = this._buildQuestionPayload(question, serverDict);
        return {
          ...question,
          ...payload,
        };
      });

      originalQuestions = updated;

      for (const question of updated) {
        this._processQuestionVisibility(question, originalQuestions, indexes);
      }

      return {
        questions: originalQuestions,
        completed: serverPayload?.completed,
        hidden: serverPayload?.hidden,
      };
    },

    getAddedVendor(questionsObject, index, childKey = "choice", type = "normal") {
      let updatedVendors = questionsObject.questions[index][childKey];

      if (type === "normal") updatedVendors.push({ name: "", website: "" });
      if (type === "withType") updatedVendors.push({ name: "", type: null, website: "" });

      return updatedVendors;
    },

    getRemovedVendor(questionsObject, mainIndex, indexOfVendor, key = "questions", childKey = "choice") {
      let question = { ...questionsObject[key][mainIndex] };

      const updated = question[childKey].filter((el, index) => index !== indexOfVendor);

      question.choice = updated;

      return updated;
    },

    getUpdatedVendor(questionsObject, vendorData, parentIndex, inputIndex, parentKey, childKey) {
      let questions = { ...questionsObject };

      let existingVendors = questions[parentKey][parentIndex][childKey];

      existingVendors[inputIndex] = {
        ...existingVendors[inputIndex],
        ...vendorData,
      };

      return existingVendors;
    },

    // HELPERS FOR CSG
    handleNumberValidation(e) {
      let charCode = e.which ? e.which : e.keyCode;

      // Allow arrow keys (left: 37, up: 38, right: 39, down: 40)
      if ([37, 38, 39, 40].includes(charCode)) {
        return;
      }

      // Allow numeric keypad digits
      if (charCode >= 96 && charCode <= 105) {
        return;
      }

      if (charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57)) e.preventDefault();

      if ([189, 69].includes(charCode)) e.preventDefault();
    },

    handleLimit(val, index, property, keyName, questionsKey) {
      this.$nextTick(() => {
        let value = Number(val);
        if (value > 100) value = 100;
        this[questionsKey] = this.updateProp(this[questionsKey], keyName, index, property, value);
      });
    },

    isPastDate(dateString) {
      const isValid = moment(dateString, "MM/YY", true).isValid();
      const expirationDate = moment(dateString, "MM/YY").endOf("month");
      const endOfMonth = moment().endOf("month");
      return !(!isValid || expirationDate.isAfter(endOfMonth));
    },

    isPastYear(yearString) {
      const isValid = moment(yearString, "YYYY", true).isValid();
      const expirationDate = moment(yearString, "YYYY").endOf("month");
      const endOfMonth = moment().endOf("month");
      return !(!isValid || expirationDate.isAfter(endOfMonth));
    },

    isFein(feinString) {
      // Trim any whitespace
      feinString = feinString.trim();
      // Accept either "12-3456789" or "123456789"
      const feinRegex = /^\d{2}-?\d{7}$/;
      return feinRegex.test(feinString);
    },

    isDateValidAndInFormat(dateString) {
      if (!dateString?.trim()) return false;

      const patterns = ["MM/DD", "M/D", "MM/D"];
      const valid = patterns.some((pattern) => {
        return moment(dateString, pattern, true).isValid();
      });

      return valid;
    },

    async saveQuickStartSetBrokerAnswers() {
      if (this.readOnlyView) {
        return;
      }
      // using for set quick start status if second section updated after sent notification to broker
      if (this.questionnaire?.extraDetails?.quickQuotes?.status == "sent_for_quick_quote") {
        this.questionnaire.extraDetails.quickQuotes.update_broker_section = true;
      }
      await this.saveExtraDetails();
      return true;
    },

    async saveQuickStartApplicationProcessAnswers() {
      if (this.readOnlyView) {
        return;
      }
      // using for set quick start status if third section updated after sent notification to broker
      if (this.questionnaire?.extraDetails?.quickQuotes?.status == "sent_for_full_quote") {
        this.questionnaire.extraDetails.quickQuotes.update_application_process_section = true;
      }
      await this.saveExtraDetails();
      return true;
    },

    /**
     * After broker send, mark the related extras flag when answers change so finals
     * show Send to Broker again (update_broker_section / update_application_process_section).
     */
    flagQuickStartBrokerDirtinessAfterAnswerChange() {
      if (this.readOnlyView) return;
      const qq = this.questionnaire?.extraDetails?.quickQuotes;
      if (!qq) return;
      if (qq.status == "sent_for_quick_quote" && !qq.update_broker_section) {
        return this.saveQuickStartSetBrokerAnswers();
      }
      if (qq.status == "sent_for_full_quote" && !qq.update_application_process_section) {
        return this.saveQuickStartApplicationProcessAnswers();
      }
    },

    redirectToCSG() {
      if (this.questionnaire?.extraDetails?.quickQuotes?.status == "sent_for_full_quote") {
        let currentUser = this.currentUser;
        currentUser.user.showQuickQuotes = false;
        this.$store.commit("login/setState", { key: "currentUser", value: currentUser }, { root: true });
        localService.setItem("userInfo", currentUser);
        this.$router.push("/user/company-setup");
      }
    },

    //
    // HANDLING PHONE NUMBERS UTILITIES
    //

    flattenPhoneNumber(phone) {
      if (!phone) return phone;

      let phoneNumber = phone.replaceAll(/[^\d]/g, "");
      if (phoneNumber.length === 11 && phoneNumber.startsWith("1")) {
        phoneNumber = phoneNumber.slice(1);
      }
      return phoneNumber;
    },

    maskPhoneNumber(phoneNumber) {
      if (!phoneNumber) return phoneNumber;
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    },

    getMaskedPhoneNumber(question, payload) {
      if (question.key === "company_phone") {
        payload.formData.answer = this.maskPhoneNumber(this.flattenPhoneNumber(payload.formData.answer));
      }

      if (question.key === "process_management_contact") {
        payload.formData.answer = this.maskPhoneNumber(this.flattenPhoneNumber(payload.formData.answer));
      }

      return payload;
    },

    getFlattenedPhoneNumbers(payload) {
      // If there are no relevant questions, avoid cloning and just return
      // the original object.
      const questions = payload.companyDetailsQuestions?.questions;
      if (!questions?.length) return payload;

      const updatedPayload = structuredClone(payload);
      const updatedQuestions = updatedPayload.companyDetailsQuestions?.questions;

      const questionKeys = ["company_phone", "process_management_contact"];

      for (const key of questionKeys) {
        const question = updatedQuestions?.find((e) => e.key === key);

        if (question) {
          question.formData.answer = this.flattenPhoneNumber(question.formData.answer);
        }
      }

      return updatedPayload;
    },
  },
};
